import React, { useState, useRef, useEffect } from 'react';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Logout,
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  Security,
  Assignment,
  Schedule,
  Person,
  Description,
  Task,
  Assessment,
  Medication,
  CalendarToday,
  Repeat,
  ShoppingCart,
} from '@mui/icons-material';
import { Avatar, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip, Chip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CarehiveHubLogo from '../common/CarehiveHubLogo';
import SessionIndicator from '../common/SessionIndicator';
import { getUserFacilityId } from '../../utils/userHelpers';
import { resolveAssetUrl } from '../../utils/asset';
import NotificationBell from '../notifications/NotificationBell';
import { isTestUi } from '../common/TestingEnvBadge';
import { useFacilityContext } from '../../contexts/FacilityContext';
import {
  getMenuItemsForUser,
  type MenuIconKey,
  type MenuItemConfig,
} from '../../config/accessControl';

const MENU_ICONS: Record<MenuIconKey, React.ReactNode> = {
  dashboard: <Dashboard className="w-5 h-5" />,
  people: <People className="w-5 h-5" />,
  business: <Business className="w-5 h-5" />,
  security: <Security className="w-5 h-5" />,
  assignment: <Assignment className="w-5 h-5" />,
  schedule: <Schedule className="w-5 h-5" />,
  person: <Person className="w-5 h-5" />,
  description: <Description className="w-5 h-5" />,
  task: <Task className="w-5 h-5" />,
  assessment: <Assessment className="w-5 h-5" />,
  medication: <Medication className="w-5 h-5" />,
  calendar: <CalendarToday className="w-5 h-5" />,
  repeat: <Repeat className="w-5 h-5" />,
  shopping: <ShoppingCart className="w-5 h-5" />,
};

const HeaderFacilitySelector: React.FC<{ role?: string }> = ({ role }) => {
  const { availableFacilities, selectedFacilityId, setSelectedFacility, isLoading } = useFacilityContext();

  // Admin should not see global selector
  if (role === 'admin') return null;
  
  // Show selector for owner-like roles and doctors if they have multiple facilities
  // For single facility, still show it but it will be disabled/read-only
  if (!availableFacilities || availableFacilities.length === 0) return null;

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedFacility(event.target.value || null);
  };

  // If only one facility, show it as read-only
  const isSingleFacility = availableFacilities.length === 1;

  return (
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: 220,
        backgroundColor: 'white',
        borderRadius: 1,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'white',
          },
        }
      }}
    >
      <InputLabel id="header-facility-selector-label" sx={{ fontSize: '0.875rem' }}>
        {isSingleFacility ? 'Facility' : 'Select Facility'}
      </InputLabel>
      <Select
        labelId="header-facility-selector-label"
        value={selectedFacilityId || availableFacilities[0]?.id || ''}
        label={isSingleFacility ? 'Facility' : 'Select Facility'}
        onChange={handleChange}
        disabled={isLoading || isSingleFacility}
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          '& .MuiSelect-select': {
            py: 1,
          }
        }}
        renderValue={(value) => {
          const facility = availableFacilities.find((f) => f.id === value);
          return facility ? facility.name : 'Select Facility';
        }}
      >
        {availableFacilities.map((facility) => (
          <MenuItem key={facility.id} value={facility.id}>
            <Tooltip title={facility.address || ''} placement="right">
              <span>{facility.name}</span>
            </Tooltip>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface DashboardLayoutProps {
  children: React.ReactNode;
}

type NavMenuItem = Omit<MenuItemConfig, 'icon'> & {
  icon: React.ReactNode;
};

const toNavMenuItem = (item: MenuItemConfig): NavMenuItem => ({
  ...item,
  icon: MENU_ICONS[item.icon],
});

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const { user, logout } = useAuth();
  const { selectedFacilityId, availableFacilities } = useFacilityContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (profileMenuOpen && profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [profileMenuOpen]);

  const assignedFacilityId = getUserFacilityId(user);
  const hasFacilityContext = !!(
    assignedFacilityId ||
    selectedFacilityId ||
    availableFacilities.length > 0
  );

  const filteredMenuItems: NavMenuItem[] = getMenuItemsForUser({
    role: user?.role,
    hasAssignedFacilityId: !!assignedFacilityId,
    hasFacilityContext,
  }).map(toNavMenuItem);
  const activeNavItems = filteredMenuItems.filter((item) => !item.comingSoon);
  const comingSoonNavItems = filteredMenuItems.filter((item) => item.comingSoon);
  const SCHEDULES_INSPECTIONS_GROUP = 'schedules_inspections';
  const comingSoonGroupedSchedulesInspections = comingSoonNavItems.filter(
    (item) => item.comingSoonGroup === SCHEDULES_INSPECTIONS_GROUP
  );
  const comingSoonOtherItems = comingSoonNavItems.filter(
    (item) => item.comingSoonGroup !== SCHEDULES_INSPECTIONS_GROUP
  );

  const is_active = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={handleDrawerToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ${
          desktopOpen ? 'w-64' : 'w-16'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col`}
      >
        {/* Logo Section */}
        <div
          className={`flex items-center border-b border-gray-200 h-16 ${
            desktopOpen ? 'justify-between px-4' : 'justify-center px-1'
          }`}
        >
          {desktopOpen && <CarehiveHubLogo variant="full" size="small" />}
          <button
            type="button"
            onClick={handleDesktopDrawerToggle}
            className="hidden lg:flex p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            aria-label={desktopOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {desktopOpen ? (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-2 flex flex-col">
          {activeNavItems.map((item) => {
            const active = is_active(item.path);
            return (
              <button
                key={`${item.path}-${item.text}`}
                type="button"
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 relative ${
                  active
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                } ${desktopOpen ? 'justify-start' : 'justify-center'}`}
              >
                {active && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r" />
                )}
                <span className={active ? 'text-white' : 'text-gray-600'}>
                  {item.icon}
                </span>
                {desktopOpen && (
                  <span className={active ? 'font-semibold' : 'font-medium'}>
                    {item.text}
                  </span>
                )}
              </button>
            );
          })}

          {comingSoonNavItems.length > 0 && (
            <>
              {desktopOpen && (
                <div
                  className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 border-t border-gray-100 mt-2"
                  role="presentation"
                >
                  Coming soon
                </div>
              )}
              {!desktopOpen && comingSoonNavItems.length > 0 && (
                <div className="mx-2 my-2 border-t border-gray-200" aria-hidden />
              )}
              {comingSoonGroupedSchedulesInspections.length > 0 && (
                <div
                  className="mx-2 my-2 rounded-lg border border-dashed border-gray-200 bg-gray-50/90 py-1"
                  role="group"
                  aria-label="Schedules and inspections, coming soon"
                >
                  {desktopOpen && (
                    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Schedules & inspections
                    </div>
                  )}
                  {comingSoonGroupedSchedulesInspections.map((item) => (
                    <Tooltip key={`soon-${item.path}-${item.text}`} title="Coming soon" placement="right">
                      <span className="block w-full">
                        <button
                          type="button"
                          disabled
                          aria-disabled="true"
                          aria-label={`${item.text}, coming soon`}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 text-gray-400 cursor-not-allowed opacity-90 ${desktopOpen ? 'justify-start' : 'justify-center'}`}
                        >
                          <span className="text-gray-400">{item.icon}</span>
                          {desktopOpen && (
                            <span className="flex flex-1 items-center justify-between gap-2 min-w-0">
                              <span className="font-medium truncate">{item.text}</span>
                              <Chip
                                label="Soon"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: 'grey.200',
                                  color: 'text.secondary',
                                }}
                              />
                            </span>
                          )}
                        </button>
                      </span>
                    </Tooltip>
                  ))}
                </div>
              )}
              {comingSoonOtherItems.map((item) => (
                <Tooltip key={`soon-${item.path}-${item.text}`} title="Coming soon" placement="right">
                  <span className="block w-full">
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      aria-label={`${item.text}, coming soon`}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 text-gray-400 cursor-not-allowed opacity-80 ${desktopOpen ? 'justify-start' : 'justify-center'}`}
                    >
                      <span className="text-gray-400">{item.icon}</span>
                      {desktopOpen && (
                        <span className="flex flex-1 items-center justify-between gap-2 min-w-0">
                          <span className="font-medium truncate">{item.text}</span>
                          <Chip
                            label="Soon"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: 'grey.200',
                              color: 'text.secondary',
                            }}
                          />
                        </span>
                      )}
                    </button>
                  </span>
                </Tooltip>
              ))}
            </>
          )}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${desktopOpen ? 'lg:ml-0' : 'lg:ml-0'}`}>
        {/* Top Navigation Bar */}
        <header className={`bg-white border-b border-gray-200 shadow-sm sticky z-[1000] ${isTestUi() ? 'top-11' : 'top-0'}`}>
          <div className="px-4 lg:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleDrawerToggle}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <MenuIcon className="w-6 h-6 text-gray-600" />
              </button>
              <div className="hidden lg:flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">
                  Healthcare Management System
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Facility Selector (compact) */}
              <HeaderFacilitySelector role={user?.role} />

              <NotificationBell />

              {/* Session Indicator */}
              <SessionIndicator />

              {/* User Info */}
              {user && (
                <div className="hidden md:block mr-2">
                  <p className="text-sm font-semibold text-gray-900">
                    {user.name || user.email}
                  </p>
                </div>
              )}

              {/* Profile Menu */}
              <div className="relative z-[1500]">
                <button
                  ref={profileButtonRef}
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative z-[1500]"
                  aria-label="User menu"
                >
                  <Avatar
                    src={user?.profile?.avatarUrl ? resolveAssetUrl(user.profile.avatarUrl) : undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                </button>

                {/* Dropdown Menu */}
                {profileMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[1400]"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <div 
                      className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[1500]" 
                      style={{ 
                        top: `${menuPosition.top}px`,
                        right: `${menuPosition.right}px`
                      }}
                    >
                      <button
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/profile');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <AccountCircle className="w-5 h-5" />
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          setProfileMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Logout className="w-5 h-5" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;

// Ensure this file is treated as a module
export {};
